// ═══════════════════════════════════════════════════════════════════════════
// Smart Rota Builder — BuildMyShifts
// Analyses historic shift patterns to auto-generate a draft rota for a new week.
// Uses availability data, AI scoring, and fairness balancing.
// ═══════════════════════════════════════════════════════════════════════════

import { getWeekStart, getWeekDates, getWeeklyHours, validateBlockAssignment, validateShiftAssignment } from './schedulingEngine';
import { isStaffAvailable } from './availabilityUtils';
import { generateAutoFillSuggestions } from './aiInsights';

/**
 * Analyse the last N weeks of shifts to find recurring patterns.
 * Returns a pattern map keyed by dayOfWeek (0-6), each containing
 * an array of "slot" objects (startTime, endTime, role, group, segments, frequency, staffHistory).
 */
export function analysePatterns(historicShifts, weeksToAnalyse = 6) {
  if (!historicShifts || historicShifts.length === 0) return {};

  // Group shifts by week
  const weekBuckets = {};
  for (const shift of historicShifts) {
    const ws = getWeekStart(shift.date);
    if (!weekBuckets[ws]) weekBuckets[ws] = [];
    weekBuckets[ws].push(shift);
  }

  // Sort weeks descending, take most recent N
  const sortedWeeks = Object.keys(weekBuckets).sort().reverse().slice(0, weeksToAnalyse);
  const totalWeeks = sortedWeeks.length;
  if (totalWeeks === 0) return {};

  // Build slot fingerprints: dayOfWeek + startTime + endTime + groupId + role
  const slotMap = {}; // fingerprint → { ...slot, occurrences, staffCounts: {staffId: count} }

  for (const ws of sortedWeeks) {
    const weekShifts = weekBuckets[ws];
    for (const shift of weekShifts) {
      if (shift.status === 'cancelled') continue;
      const d = new Date(shift.date);
      const dow = (d.getDay() + 6) % 7; // 0=Mon, 6=Sun (ISO week)
      const fp = `${dow}|${shift.startTime}|${shift.endTime}|${shift.groupId || ''}|${shift.requiredRole || ''}`;

      if (!slotMap[fp]) {
        slotMap[fp] = {
          dayOfWeek: dow,
          startTime: shift.startTime,
          endTime: shift.endTime,
          groupId: shift.groupId || null,
          groupName: shift.groupName || '',
          groupColor: shift.groupColor || '',
          requiredRole: shift.requiredRole || '',
          requiredSkills: shift.requiredSkills || [],
          blockType: shift.blockType || 'single',
          segments: shift.segments || [],
          occurrences: 0,
          staffCounts: {},
        };
      }

      slotMap[fp].occurrences++;
      if (shift.assignedStaffId) {
        slotMap[fp].staffCounts[shift.assignedStaffId] =
          (slotMap[fp].staffCounts[shift.assignedStaffId] || 0) + 1;
      }
    }
  }

  // Convert to array grouped by dayOfWeek, filter to slots that appear in ≥40% of weeks
  const minOccurrences = Math.max(1, Math.ceil(totalWeeks * 0.4));
  const patterns = {};

  for (const slot of Object.values(slotMap)) {
    if (slot.occurrences < minOccurrences) continue;
    const dow = slot.dayOfWeek;
    if (!patterns[dow]) patterns[dow] = [];

    // Rank staff by frequency (most common assignee first)
    const rankedStaff = Object.entries(slot.staffCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([staffId, count]) => ({ staffId, count, frequency: count / slot.occurrences }));

    patterns[dow].push({
      ...slot,
      frequency: slot.occurrences / totalWeeks,
      rankedStaff,
    });
  }

  return patterns;
}

/**
 * Generate draft shifts for a target week based on detected patterns.
 *
 * @param {string} targetWeekStart  - YYYY-MM-DD Monday of the target week
 * @param {object} patterns         - Output of analysePatterns()
 * @param {Array}  allStaff         - Active staff array
 * @param {Array}  existingShifts   - Already-existing shifts for the target week
 * @param {Array}  availability     - All availability records
 * @param {Array}  historicShifts   - Historic shifts (for AI scoring)
 * @param {object} staffProfiles    - { staffId: profileObj }
 * @param {object} config           - Scheduling config
 * @returns {Array} Array of draft shift objects (not yet saved to Firestore)
 */
export function generateSmartRota(
  targetWeekStart,
  patterns,
  allStaff,
  existingShifts,
  availability,
  historicShifts,
  staffProfiles,
  config,
) {
  const weekDates = getWeekDates(targetWeekStart);
  const drafts = [];
  // Track assignments as we go so we don't double-book within the generated set
  const pendingShifts = [...existingShifts];

  for (let dow = 0; dow < 7; dow++) {
    const date = weekDates[dow];
    const dayPatterns = patterns[dow] || [];

    for (const pattern of dayPatterns) {
      // Skip if an identical shift already exists for this date
      const alreadyExists = existingShifts.some(
        s => s.date === date &&
             s.startTime === pattern.startTime &&
             s.endTime === pattern.endTime &&
             (s.groupId || '') === (pattern.groupId || '')
      );
      if (alreadyExists) continue;

      // Build the draft shift (no staff yet)
      const draft = {
        date,
        startTime: pattern.startTime,
        endTime: pattern.endTime,
        blockType: pattern.blockType,
        segments: pattern.segments.length > 0 ? pattern.segments : [],
        groupId: pattern.groupId,
        groupName: pattern.groupName,
        groupColor: pattern.groupColor,
        requiredRole: pattern.requiredRole,
        requiredSkills: pattern.requiredSkills,
        assignedStaffId: null,
        assignedStaffName: '',
        status: 'unassigned',
        notes: '',
        companyId: null, // filled in by caller
        rotaId: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _pattern: {
          frequency: pattern.frequency,
          rankedStaff: pattern.rankedStaff,
        },
      };

      // Try to assign the best staff member
      const assignment = pickBestStaff(
        draft, pattern, allStaff, pendingShifts, availability, historicShifts, staffProfiles, config
      );

      if (assignment) {
        draft.assignedStaffId = assignment.staffId;
        draft.assignedStaffName = assignment.staffName;
        draft.status = 'assigned';
        draft._confidence = assignment.confidence;
        draft._score = assignment.score;
        draft._reason = assignment.reason;
      } else {
        draft._confidence = 'none';
        draft._score = 0;
        draft._reason = 'No available staff found';
      }

      pendingShifts.push(draft);
      drafts.push(draft);
    }
  }

  return drafts;
}

/**
 * Pick the best staff member for a pattern-based shift.
 * Priority: historic most-frequent assignee → AI score ranking.
 * Respects availability and validation constraints.
 */
function pickBestStaff(draft, pattern, allStaff, pendingShifts, availability, historicShifts, staffProfiles, config) {
  // 1) Try pattern-ranked staff first (most frequent assignees)
  for (const candidate of pattern.rankedStaff) {
    const member = allStaff.find(s => s.id === candidate.staffId);
    if (!member || member.active === false) continue;

    // Check availability
    if (!isStaffAvailable(candidate.staffId, draft.date, draft.startTime, draft.endTime, availability)) continue;

    // Check scheduling constraints (double-booking, rest periods, max hours)
    const profile = staffProfiles[candidate.staffId] || {};
    const validation = draft.segments?.length > 0
      ? validateBlockAssignment(candidate.staffId, draft, pendingShifts, profile, config)
      : validateShiftAssignment(candidate.staffId, draft, pendingShifts, profile, config);

    if (!validation.valid) continue;

    return {
      staffId: candidate.staffId,
      staffName: member.displayName || member.username || '',
      confidence: candidate.frequency >= 0.7 ? 'high' : candidate.frequency >= 0.4 ? 'medium' : 'low',
      score: Math.round(candidate.frequency * 100),
      reason: `Worked this slot ${Math.round(candidate.frequency * 100)}% of recent weeks`,
    };
  }

  // 2) Fall back to AI auto-fill scoring
  const suggestions = generateAutoFillSuggestions(draft, allStaff, pendingShifts, {}, staffProfiles, config);
  const available = suggestions.filter(s => s.available);

  // Also filter by availability records
  for (const sug of available) {
    if (!isStaffAvailable(sug.staffId, draft.date, draft.startTime, draft.endTime, availability)) continue;

    return {
      staffId: sug.staffId,
      staffName: sug.staffName || sug.displayName || '',
      confidence: sug.confidence,
      score: sug.score,
      reason: sug.reasonList?.[0] || 'AI recommended',
    };
  }

  return null; // No suitable staff found
}

/**
 * Produce a summary of what the smart rota builder generated.
 */
export function summariseSmartRota(drafts) {
  const total = drafts.length;
  const assigned = drafts.filter(d => d.assignedStaffId).length;
  const unassigned = total - assigned;
  const highConf = drafts.filter(d => d._confidence === 'high').length;
  const medConf = drafts.filter(d => d._confidence === 'medium').length;
  const lowConf = drafts.filter(d => d._confidence === 'low' || d._confidence === 'none').length;

  // Group by day
  const byDay = {};
  for (const d of drafts) {
    const dow = (new Date(d.date).getDay() + 6) % 7;
    byDay[dow] = (byDay[dow] || 0) + 1;
  }

  return {
    total,
    assigned,
    unassigned,
    highConf,
    medConf,
    lowConf,
    byDay,
  };
}
